// Pricing tiers + feature matrix data, extracted from PricingPage.tsx so
// the build-time SEO prerender plugin (scripts/inject-seo-plugin.ts) and
// the React page render from one source — no drift between what a crawler
// sees in prerendered HTML and what a user sees after hydration (A2F-938).
//
// Pure data only — no JSX/React imports — so the Node-side Vite plugin can
// import it at build time. The enterprise feature *icons* stay in
// PricingPage.tsx (mapped onto ENTERPRISE_FEATURES by index).

// Per-page SEO strings — shared by PricingPage's <Helmet> and the build-time
// prerender plugin so the title/description match in both the hydrated app
// and the prerendered pricing.html a crawler sees.
export const PRICING_SEO = {
  title: 'Pricing — Chrona Pro | Free, Trial & Pro plans',
  description:
    'Compare Chrona Pro plans: free document formatting, a 14-day full-feature trial, and Pro at $99/year with AI rule generation, FDA terminology enforcement, and regulatory packs for Microsoft Word.',
}

export interface Tier {
  id: string
  name: string
  sub: string
  tagline: string
  badge?: string
  features: string[]
  cta: string
  highlight: boolean
}

export const TIERS: Tier[] = [
  {
    id: 'free',
    name: 'Free',
    sub: 'Personal',
    tagline: 'Powerful formatting for your documents.',
    features: [
      'Full document formatting — tables, headings, body text, captions, lists',
      'Custom table colors and saved profiles',
      'IND Section Headings pack',
    ],
    cta: 'Get started',
    highlight: false,
  },
  {
    id: 'trial',
    name: 'Trial',
    sub: '14 days free',
    tagline: 'Experience the full power of Pro — no commitment.',
    badge: 'MOST POPULAR',
    features: [
      'Everything in Pro',
      'No credit card required',
      'Use on real documents, no limits',
    ],
    cta: 'Start free trial',
    highlight: true,
  },
  {
    id: 'pro',
    name: 'Pro',
    sub: '$99/year',
    tagline: 'Advanced tools and regulatory confidence.',
    features: [
      'AI-powered rule generation — describe a check, get a taskpane button',
      'Terminology enforcement (FDA, ICH conventions)',
      'Regulatory formatting packs (eCTD, cover letter validation)',
      'Custom scan & search rules',
      'Priority support',
    ],
    cta: 'Start free trial',
    highlight: false,
  },
]

// Enterprise feature labels (icons are mapped by index in PricingPage.tsx).
export const ENTERPRISE_FEATURES: string[] = [
  'Shared rules across authors',
  'Cross-document consistency',
  'FDA response intelligence',
  'Part 11 compliance',
]

export type Access = 'all' | 'pro' | 'ent' | 'text'

export interface MatrixRow {
  feature: string
  detail?: string
  access: Access
  // For 'text' rows, the value shown in each column
  textValues?: [string, string, string]
}

export interface MatrixSection {
  category: string
  rows: MatrixRow[]
}

export const MATRIX: MatrixSection[] = [
  {
    category: 'Table formatting',
    rows: [
      { feature: 'One-click table formatting', detail: 'Apply to all tables in one click', access: 'all' },
      { feature: 'Chrona Light & Dark presets', access: 'all' },
      { feature: 'Custom table colors', detail: 'Header, rows, border color and weight', access: 'all' },
      { feature: 'Multiple saved table profiles', access: 'all' },
    ],
  },
  {
    category: 'Document formatting',
    rows: [
      { feature: 'Heading styles', detail: 'Font, size, color, spacing per level', access: 'all' },
      { feature: 'Numbered headings', detail: 'Auto-numbered hierarchy', access: 'all' },
      { feature: 'Body text formatting', detail: 'Font, size, line and paragraph spacing', access: 'all' },
      { feature: 'Caption & figure formatting', access: 'all' },
      { feature: 'Lists & indentation', access: 'all' },
    ],
  },
  {
    category: 'Regulatory packs',
    rows: [
      { feature: 'IND Section Headings', detail: 'Browse and insert CTD sections from taskpane', access: 'all' },
      { feature: 'FDA terminology enforcement', detail: '"active substance" → "drug substance", etc.', access: 'pro' },
      { feature: 'eCTD formatting conventions', detail: 'Pre-built formatting rules per FDA/ICH guidance', access: 'pro' },
      { feature: 'Cover letter validation', detail: 'Required elements per FDA Technical Conformance Guide', access: 'pro' },
    ],
  },
  {
    category: 'AI-powered rules',
    rows: [
      { feature: 'Natural language rule builder', detail: 'Describe a check, get a taskpane button', access: 'pro' },
      { feature: 'Custom scan rules', detail: 'Hidden text, alt text, font checks', access: 'pro' },
      { feature: 'Custom search rules', detail: 'Grammar, placeholders, custom terminology', access: 'pro' },
    ],
  },
  {
    category: 'Team & compliance',
    rows: [
      { feature: 'Section owner assignment', detail: '[CHRONA:OWNER:Name] comment insertion', access: 'ent' },
      { feature: 'Shared rules across authors', detail: 'Org-wide style profile, locked fields', access: 'ent' },
      { feature: 'Cross-document consistency', detail: 'Abbreviations, compound code, dose/unit, cross-refs', access: 'ent' },
      { feature: 'FDA response intelligence', detail: 'Chrona Resolve — letter analysis and section mapping', access: 'ent' },
      { feature: '21 CFR Part 11 compliance', detail: 'Audit trail, readiness snapshots, override governance', access: 'ent' },
    ],
  },
  {
    category: 'Account & support',
    rows: [
      { feature: 'Chrona account required', access: 'text', textValues: ['Yes', 'Yes', 'Yes'] },
      { feature: 'Priority support', access: 'pro' },
    ],
  },
]
